import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {FontCategory} from '@Libraries/font-library';
import {initFonts} from '@Libraries/font-library';
import {FontUploadSection} from '@Components/font-selector-panel/components/font-upload-section';
import {MyDesignFontSection} from '@Components/font-selector-panel/components/my-design-font-section';
import {AllFontsSection} from '@Components/font-selector-panel/components/all-fonts-section';
import type {PanelFooterProps} from '@Components/panel';
import {Panel, PanelHeader} from '@Components/panel';
import {FontSearchSection} from '@Components/font-selector-panel/components/font-search-section';
import {FontsBrandSection} from '@Components/font-selector-panel/components/fonts-brand-section';
import type {FontSelectorHandlers, FontSelectorProperties} from '@Components/font-selector-panel/font-selector.types';
import {ScrollToTop} from '@Components/scroll-to-top';
import {IconSize} from '@Components/icon-v2/icon.types';
import {RecommendedFontsSection} from '@Components/font-selector-panel/components/recommended-fonts-section';
import {InputField} from '@Components/input-field-v2';
import {InputControlItemType} from '@Components/input-field-v2/input-field.types';
import styles from './font-selector-panel.module.scss';
import {TrySeasonalFontButton} from '@Components/font-selector-panel/components/try-seasonal-font-button/try-seasonal-font-button';

export interface FontSelectorPanelProps extends FontSelectorHandlers, FontSelectorProperties {
  className?: string;
  fontsInDesign?: string[];
  /**
   * If this is sent, then the behavior becomes dumb for selection
   */
  selectedFontFamily?: string;
  isSelectedFontLoading?: boolean;
  showUserFonts?: boolean;
  showFontsInDesign?: boolean;
  showDividerAfterBrandsSection?: boolean;
  panelFooter?: ReactElement<PanelFooterProps> | null;
  scrollToTopButtonClassName?: string;
}

const SCROLL_BUTTON_OFFSET = 400;

export function FontSelectorPanel({
  className = '',
  onBrandItemClicked = (): void => {},
  onBackButtonCallback = (): void => {},
  onCloseButtonCallback = (): void => {},
  onSelectFontFamily = (): void => {},
  onDeleteFontItem = (): void => {},
  onFileUploaded = (): void => {},
  onAddBrandAsset = (): void => {},
  showDropdownVariant = false,
  showHeader = false,
  selectedFontFamily = '',
  isSelectedFontLoading = false,
  fontsInDesign = [],
  showSmallDropdownForBrands = false,
  showUserFonts = true,
  scriptForRecommendedFonts = '',
  showBackButton = true,
  showCloseButton = false,
  headerText = window.i18next.t('pmwjs_font'),
  hasSmallTopPadding = false,
  showFontsInDesign = true,
  showDividerAfterBrandsSection = true,
  panelFooter = null,
  scrollToTopButtonClassName = '',
}: FontSelectorPanelProps): ReactElement<any> {
  const [selectedCategory, setSelectedCategory] = useState<FontCategory | undefined>(undefined);

  const [onSearchFocused, setOnSearchFocused] = useState(false);

  useEffect(() => {
    initFonts().catch(() => {
      window.PMW.openErrorModal({
        message: window.i18next.t('pmwjs_error_message'),
      });
    });
  }, []);

  const onSearchFocus = (): void => {
    setOnSearchFocused(true);
  };

  const onBackButton = (): void => {
    if (onSearchFocused) {
      setOnSearchFocused(false);
      return;
    }

    onBackButtonCallback();
  };

  const showContent = (): ReactElement<any> => {
    if (showDropdownVariant) {
      return (
        <AllFontsSection
          selectedFontFamily={selectedFontFamily}
          isSelectedFontLoading={isSelectedFontLoading}
          showUserFonts={showUserFonts}
          onSelectFontFamily={onSelectFontFamily}
          showHeading={false}
        />
      );
    }
    return showDefaultVariant();
  };

  const onTrySeasonalFontButtonClicked = (selectedCategory: FontCategory): void => {
    setSelectedCategory(selectedCategory);
    onSearchFocus();
  };

  const showDefaultVariant = (): ReactElement<any> => {
    if (onSearchFocused) {
      return (
        <FontSearchSection
          hasSmallTopPadding={hasSmallTopPadding}
          isSelectedFontLoading={isSelectedFontLoading}
          onSelectFontFamily={onSelectFontFamily}
          selectedFontFamily={selectedFontFamily}
          selectedCategory={selectedCategory}
          autoFocusSearchInputField={!selectedCategory}
        />
      );
    }
    return (
      <>
        <FontsBrandSection
          onBrandItemClicked={onBrandItemClicked}
          onAddBrandAsset={(): void => {
            onAddBrandAsset();
          }}
          showSmallDropdownForBrands={showSmallDropdownForBrands}
          hasSmallPadding={hasSmallTopPadding}
          showDividerAfterBrandsSection={showDividerAfterBrandsSection}
        />
        <InputField
          input=""
          placeholder={window.i18next.t('pmwjs_start_search')}
          controls={{
            showDefaultControls: false,
            showSingleDefaultControlOnly: false,
            customControls: [
              {
                id: 'search-icon',
                type: InputControlItemType.ICONS_ONLY,
                icon: 'icon-search',
              },
            ],
          }}
          onInputFocus={onSearchFocus}
          className="spacing-m-t-4 spacing-m-b-3"
        />
        <TrySeasonalFontButton onClick={onTrySeasonalFontButtonClicked} />
        <RecommendedFontsSection
          script={scriptForRecommendedFonts}
          selectedFontFamily={selectedFontFamily}
          onSelectFontFamily={onSelectFontFamily}
          className="spacing-p-t-3 spacing-p-b-3"
        />
        {showFontsInDesign ? (
          <MyDesignFontSection
            selectedFontFamily={selectedFontFamily}
            onSelectFontFamily={onSelectFontFamily}
            className="spacing-p-t-4 spacing-p-b-3"
            fontsInDesign={fontsInDesign}
          />
        ) : null}
        <FontUploadSection
          onFileUploadedCallback={onFileUploaded}
          onSelectFontFamily={onSelectFontFamily}
          onDeleteFontItem={onDeleteFontItem}
          className="spacing-p-t-1 spacing-p-b-1"
          selectedFontFamily={selectedFontFamily}
          isSelectedFontLoading={isSelectedFontLoading}
        />
        <AllFontsSection selectedFontFamily={selectedFontFamily} isSelectedFontLoading={isSelectedFontLoading} onSelectFontFamily={onSelectFontFamily} className="spacing-p-t-4" />
      </>
    );
  };

  const getContent = (): ReactElement<any> => {
    return (
      <ScrollToTop scrollTopOffset={SCROLL_BUTTON_OFFSET} className={`${styles.fontSelectorContainer} ${className}`} scrollButtonClassName={scrollToTopButtonClassName}>
        {showContent()}
      </ScrollToTop>
    );
  };

  const getHeaderText = (): string => {
    return onSearchFocused ? window.i18next.t('Search') : headerText;
  };

  const getHeader = (): ReactElement<any> | null => {
    if (showDropdownVariant) {
      return null;
    }
    if (!showHeader) {
      return null;
    }
    return (
      <PanelHeader
        title={getHeaderText()}
        showCloseButton={showCloseButton}
        showBackButton={onSearchFocused ? true : showBackButton}
        backButtonIconSize={IconSize.SIZE_ICON_20}
        centerAlignTitle
        onBackButton={onBackButton}
        onClose={onCloseButtonCallback}
        titleClasses="body-lg-bold"
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} panelFooter={panelFooter} />;
}
